import type { CSSProperties, FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

export interface Readout {
    label: string
    value: ReactNode
}

interface PageHeaderProps {
    fig: string
    title: string
    detail: string
    note: string
    lines: Array<string>
    intro?: ReactNode
    readouts?: Array<Readout>
    className?: string
    children?: ReactNode
}

const delay = (value: number): CSSProperties => ({ '--delay': `${value}ms` } as CSSProperties)

/**
 * Page opener: the figure label with its dim mono line, a large headline whose last line is dim,
 * an optional intro and a column of readouts. Animates once on load without script.
 */
export const PageHeader: FC<PageHeaderProps> = ({ fig, title, detail, note, lines, intro, readouts, className, children }): ReactNode => {
    const last = lines.length - 1

    return (
        <header className={cn('border-b border-hairline', className)}>
            <div className='grid gap-10 px-gutter pb-14 pt-12 desk:grid-cols-[minmax(0,1fr)_minmax(260px,auto)] desk:gap-16 desk:pb-20 desk:pt-20'>
                <div>
                    <div data-ruler-mark={fig} className='rise-fade' style={delay(0)}>
                        <p className='hud text-ink'>
                            Fig. {fig} / {title} / {detail}
                        </p>

                        <p className='hud text-mute'>
                            {note}
                        </p>
                    </div>

                    <h1 className='display-xl mt-10'>
                        {lines.map((line, index) => (
                            <span key={`${line}-${index}`} className='rise-line'>
                                <span className={cn('rise-inner', index === last ? 'text-dim' : 'text-ink')} style={delay(80 + index * 90)}>
                                    {line}
                                </span>
                            </span>
                        ))}
                    </h1>

                    {intro && (
                        <div className='rise-fade body-copy mt-8 text-ink/85' style={delay(360)}>
                            {intro}
                        </div>
                    )}

                    {children}
                </div>

                {readouts && readouts.length > 0 && (
                    <dl className='rise-fade grid content-end gap-x-8 gap-y-3 self-end sm:grid-cols-2 desk:grid-cols-1' style={delay(420)}>
                        {readouts.map(readout => (
                            <div key={readout.label} className='flex items-baseline justify-between gap-6 border-t border-hairline pt-3 desk:min-w-[280px]'>
                                <dt className='hud text-mute'>
                                    {readout.label}
                                </dt>

                                <dd className='hud whitespace-nowrap text-ink'>
                                    {readout.value}
                                </dd>
                            </div>
                        ))}
                    </dl>
                )}
            </div>
        </header>
    )
}
